// PAPERLIKE CUSTOM — Paperlike-built, no vanilla equivalent

if (!window.customElements.get('blog-recent-articles')) {
  // Mirror Shopify's `handleize` filter so client-side tag comparisons match
  // the server-rendered URLs and `data-tag` attrs. Lowercase, collapse any run
  // of non-alphanumeric chars to a single `-`, strip leading/trailing dashes.
  function handleize(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ── Search helpers (2026-09-02) ─────────────────────────────────────────
  // Lowercase, strip diacritics, collapse anything non-alphanumeric to a space.
  function normalizeText(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  // Damerau-Levenshtein distance <= 1 (one insert / delete / substitute / adjacent swap).
  function withinOneEdit(a, b) {
    if (a === b) return true;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    if (la === lb) {
      var diffs = [];
      for (var i = 0; i < la; i++) {
        if (a[i] !== b[i]) {
          diffs.push(i);
          if (diffs.length > 2) return false;
        }
      }
      if (diffs.length === 1) return true;
      // transposition: exactly two adjacent mismatches, swapped
      return diffs.length === 2 && diffs[1] === diffs[0] + 1 &&
        a[diffs[0]] === b[diffs[1]] && a[diffs[1]] === b[diffs[0]];
    }
    // one insertion/deletion
    var longer = la > lb ? a : b;
    var shorter = la > lb ? b : a;
    var i2 = 0, j = 0, skipped = false;
    while (i2 < shorter.length && j < longer.length) {
      if (shorter[i2] === longer[j]) { i2++; j++; }
      else if (skipped) return false;
      else { skipped = true; j++; }
    }
    return true;
  }

  // One query token vs one card index: substring, space-less substring
  // ("brushset" → "brush set"), or — from 4 chars — a typo-tolerant match
  // against any word / word prefix ("procreat", "prcreate", "notabilty").
  function tokenMatches(token, idx) {
    if (idx.text.indexOf(token) !== -1) return true;
    if (idx.compact.indexOf(token) !== -1) return true;
    if (token.length < 4) return false;
    var words = idx.words;
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (w.length < token.length - 1) continue;
      if (withinOneEdit(token, w)) return true;
      if (w.length > token.length && withinOneEdit(token, w.slice(0, token.length))) return true;
    }
    return false;
  }

  function debounce(fn, wait) {
    var t;
    return function() {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(ctx, args); }, wait);
    };
  }

  class BlogRecentArticles extends HTMLElement {
    connectedCallback() {
      this.perPage        = parseInt(this.dataset.perPage, 10) || 9;
      this.blogPath       = this.dataset.blogPath;           // e.g. /blogs/news or /blogs/news/tagged/foo
      this.baseBlogPath   = this.dataset.baseBlogPath;       // always /blogs/news
      this.totalArticles  = parseInt(this.dataset.totalArticles, 10) || 0;

      this.allCards        = Array.from(this.querySelectorAll('[data-article-card]'));
      this.tabs            = this.querySelectorAll('[data-tab-id]');
      this.loadMoreWrapper = this.querySelector('.blog-recent-articles__load-more');
      this.loadMoreBtn     = this.loadMoreWrapper?.querySelector('.button, button');
      this.activeTag       = null;
      this._fetching       = false;
      this._fetchPage      = 1; // last blog page fetched
      this._fetchExhausted = false; // true once fetch returns an empty page
      this._waitingToShow  = false; // user clicked while prefetch was still in flight

      // Handles already in DOM — used to deduplicate AJAX responses
      this._loadedHandles = new Set(
        this.allCards.map(c => c.dataset.articleHandle).filter(Boolean)
      );

      // Initialise shown count from Liquid-rendered visible cards
      this._shownCount = this.allCards.filter(c => !c.hidden).length;

      this.tabs.forEach(tab => tab.addEventListener('click', () => this.activateTab(tab)));
      this.loadMoreBtn?.addEventListener('click', () => this.loadMore());

      // If the page was loaded with a URL tag (e.g. /blogs/news/tagged/cat--drawing),
      // auto-activate the matching tab and re-run visibility so _shownCount stays in sync
      // with the filtered card set. Runs before first paint so there is no visible flash.
      const urlTag = this.dataset.currentTag;
      if (urlTag) {
        const match = Array.from(this.tabs).find(t => t.dataset.tag === urlTag);
        if (match) {
          this.tabs.forEach(t => {
            const active = t === match;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', String(active));
          });
          this.activeTag = match.dataset.tag;
          this.resetVisibility();
        }
      }

      // Kick off background prefetch immediately so the next batch is ready before the user needs it
      this._prefetchIfNeeded();

      this._searchTokens = [];   // normalized query tokens; empty = search inactive
      this._searchQuery  = '';
      this._allLoaded    = false;
      this._initSearch();
    }

    // ── Search (2026-09-02) ───────────────────────────────────────────────
    // Fuzzy client-side search over title + excerpt + tags of every article.
    // Focusing the field drains the remaining ?view=fetch pages so the whole
    // blog is searchable; results reuse the tab filter + load-more machinery.
    _initSearch() {
      this.searchInput  = this.querySelector('[data-blog-search-input]');
      if (!this.searchInput) return;
      this.searchClear  = this.querySelector('[data-blog-search-clear]');
      this.searchStatus = this.querySelector('[data-blog-search-status]');
      this.searchEmpty  = this.querySelector('[data-blog-search-empty]');
      this.resultsText  = this.dataset.searchResultsText || '{count} articles for \u201c{query}\u201d';
      this.emptyText    = this.searchEmpty ? this.searchEmpty.textContent.trim() : '';

      const run = debounce(() => this._applySearch(this.searchInput.value), 120);

      this.searchInput.addEventListener('focus', () => { this._ensureAllLoaded(); });
      this.searchInput.addEventListener('input', () => {
        if (this.searchClear) this.searchClear.hidden = this.searchInput.value.length === 0;
        run();
      });
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        if (this.searchInput.value) this._clearSearch(true);
        else this.searchInput.blur();
      });
      // Native "x" of type=search (where a browser still shows it) fires `search`
      this.searchInput.addEventListener('search', () => {
        if (!this.searchInput.value) this._clearSearch(false);
      });
      this.searchClear?.addEventListener('click', () => this._clearSearch(true));
    }

    _clearSearch(refocus) {
      this.searchInput.value = '';
      if (this.searchClear) this.searchClear.hidden = true;
      this._applySearch('');
      if (refocus) this.searchInput.focus();
    }

    _applySearch(raw) {
      const q = normalizeText(raw);
      const wasActive = this._searchTokens.length > 0;
      this._searchQuery  = raw.trim();
      this._searchTokens = q.length >= 2 ? q.split(' ') : [];
      const active = this._searchTokens.length > 0;

      // A fresh search means "across all topics" — snap the tab back to All so
      // an active category never silently hides matches. Tabs stay usable to
      // narrow the results afterwards.
      if (active && !wasActive && this.activeTag) {
        const allTab = Array.from(this.tabs).find(t => t.dataset.tabId === 'all');
        if (allTab) this.activateTab(allTab); // calls resetVisibility()
        else this.resetVisibility();
      } else {
        this.resetVisibility();
      }

      if (active && !this._allLoaded) {
        this._ensureAllLoaded().then(() => {
          // Re-run only if this query is still the current one
          if (this._searchQuery === raw.trim()) this.resetVisibility();
        });
      }
    }

    _cardIndex(card) {
      if (card._plSearch) return card._plSearch;
      const parts = Array.from(card.querySelectorAll('.blog-post-card__info p')).map(p => p.textContent);
      parts.push((card.dataset.tags || '').replace(/,/g, ' '));
      const text = normalizeText(parts.join(' '));
      card._plSearch = {
        text,
        compact: text.replace(/ /g, ''),
        words: Array.from(new Set(text.split(' ').filter(Boolean)))
      };
      return card._plSearch;
    }

    _cardMatchesSearch(card) {
      if (!this._searchTokens.length) return true;
      const idx = this._cardIndex(card);
      return this._searchTokens.every(token => tokenMatches(token, idx));
    }

    _updateSearchUi() {
      if (!this.searchInput) return;
      const active = this._searchTokens.length > 0;
      const count  = this.filteredCards.length;
      const loading = active && !this._allLoaded;
      if (this.searchStatus) {
        this.searchStatus.hidden = !active;
        if (active) {
          this.searchStatus.textContent = this.resultsText
            .replace('{count}', String(count))
            .replace('{query}', this._searchQuery) + (loading ? ' \u2026' : '');
        }
      }
      if (this.searchEmpty) {
        this.searchEmpty.hidden = !(active && count === 0 && !loading);
        if (!this.searchEmpty.hidden) {
          this.searchEmpty.textContent = this.emptyText.replace('{query}', this._searchQuery);
        }
      }
      this.classList.toggle('is-searching', active);
    }

    // Drain every remaining ?view=fetch page (once). Resolves when the whole
    // blog is in the DOM or the feed reports the end.
    _ensureAllLoaded() {
      if (this._allLoadedPromise) return this._allLoadedPromise;
      this._allLoadedPromise = (async () => {
        const wait = ms => new Promise(r => setTimeout(r, ms));
        let guard = 0;
        while (!this._fetchExhausted && this.allCards.length < this.totalArticles && guard++ < 60) {
          if (this._fetching) { await wait(60); continue; }
          if (!this.blogPath) break;
          await this._fetchNextPage();
        }
        this._allLoaded = true;
        this._updateSearchUi();
        this._updateLoadMore();
      })();
      return this._allLoadedPromise;
    }

    activateTab(tab) {
      this.tabs.forEach(t => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active);
      });
      this.activeTag = tab.dataset.tag || null;
      this.resetVisibility();
    }

    get filteredCards() {
      let cards = this.allCards;
      if (this.activeTag) {
        cards = cards.filter(card =>
          card.dataset.tags.split(',').map(t => handleize(t)).includes(this.activeTag)
        );
      }
      if (this._searchTokens && this._searchTokens.length) {
        cards = cards.filter(card => this._cardMatchesSearch(card));
      }
      return cards;
    }

    resetVisibility() {
      this.allCards.forEach(card => { card.hidden = true; });
      const visible = this.filteredCards;
      visible.slice(0, this.perPage).forEach(card => { card.hidden = false; });
      this._shownCount = Math.min(this.perPage, visible.length);
      this._updateLoadMore();
      this._updateSearchUi();
    }

    loadMore() {
      const visible = this.filteredCards;
      const next    = visible.slice(this._shownCount, this._shownCount + this.perPage);

      if (next.length > 0) {
        // Cards already in DOM — show immediately
        next.forEach(card => { card.hidden = false; });
        this._shownCount += next.length;
        this._updateLoadMore();

        // Start next prefetch when we're getting low on hidden cards
        const hiddenRemaining = visible.length - this._shownCount;
        if (hiddenRemaining < this.perPage) {
          this._prefetchIfNeeded();
        }
      } else if (this._fetching) {
        // Prefetch is still in flight — hold the click, re-fire once data arrives
        this._waitingToShow = true;
        if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('disabled', '');
      } else if (this._fetchExhausted || this.allCards.length >= this.totalArticles) {
        if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
      } else {
        // No matching cards in DOM yet, but more server pages exist — fetch and wait
        this._prefetchIfNeeded();
        if (this._fetching) {
          this._waitingToShow = true;
          if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('disabled', '');
        } else {
          if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
        }
      }
    }

    _prefetchIfNeeded() {
      if (this._fetching || this._fetchExhausted || !this.blogPath) return;
      if (this.allCards.length < this.totalArticles || this.dataset.urlFiltered) {
        this._fetchNextPage();
      }
    }

    _updateLoadMore() {
      if (!this.loadMoreWrapper) return;
      // While a search is still draining the feed, the button would only mean
      // "wait" — keep it hidden until every article is in the DOM.
      if (this._searchTokens && this._searchTokens.length && !this._allLoaded) {
        this.loadMoreWrapper.hidden = true;
        return;
      }
      const allShown       = this._shownCount >= this.filteredCards.length;
      const noMoreOnServer = this._fetchExhausted || this.allCards.length >= this.totalArticles;
      this.loadMoreWrapper.hidden = allShown && noMoreOnServer;
    }

    async _fetchNextPage() {
      if (this._fetching || !this.blogPath) return;
      this._fetching = true;

      const nextPage = this._fetchPage + 1;
      // Fetch via the ?view=fetch alternative template (blog.fetch.json + blog-articles-feed.liquid).
      // This uses {% paginate %} so it serves all articles beyond the 50-article section limit,
      // without requiring the main-blog section to be visible on the blog page.
      const url = `${this.blogPath}?view=fetch&page=${nextPage}`;

      try {
        const response = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        const blogGrid = doc.querySelector('.blog-posts');
        if (!blogGrid) throw new Error('.blog-posts not found in response');

        const fetched = Array.from(blogGrid.querySelectorAll('blog-post-card[data-article-handle]'));

        // Deduplicate: skip articles already rendered in our section
        const fresh = fetched.filter(card => {
          const handle = card.dataset.articleHandle;
          return handle && !this._loadedHandles.has(handle);
        });

        if (fresh.length > 0) {
          const grid = this.querySelector('.blog-recent-articles__grid');
          fresh.forEach(card => {
            const handle = card.dataset.articleHandle;
            const tags   = card.dataset.tags || '';
            const wrapper = document.createElement('div');
            wrapper.dataset.articleCard   = '';
            wrapper.dataset.articleHandle = handle;
            wrapper.dataset.tags          = tags;
            wrapper.hidden                = true;
            wrapper.appendChild(card.cloneNode(true));
            grid.appendChild(wrapper);
            this._loadedHandles.add(handle);
          });

          this.allCards   = Array.from(this.querySelectorAll('[data-article-card]'));
          this._fetchPage = nextPage;
          this._fetching  = false;

          if (this._waitingToShow) {
            // User clicked while we were fetching — show the cards now
            this._waitingToShow = false;
            if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
            this.loadMore();
          } else {
            this._updateLoadMore();
          }
        } else {
          this._fetchPage = nextPage;
          this._fetching  = false;

          if (fetched.length === 0) {
            // Empty page — we've reached the true end (handles tagged URLs where totalArticles is the whole-blog count)
            this._fetchExhausted = true;
            this._waitingToShow  = false;
            if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
            this._updateLoadMore();
          } else if (this.allCards.length < this.totalArticles) {
            // Non-empty page but all articles already in DOM — skip to next page
            this._fetchNextPage();
          } else {
            this._waitingToShow = false;
            if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
          }
        }
      } catch (e) {
        console.warn('blog-recent-articles: fetch failed', e);
        this._fetching      = false;
        this._waitingToShow = false;
        if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
        if (this._shownCount >= this.filteredCards.length) {
          if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
        }
      }
    }
  }

  customElements.define('blog-recent-articles', BlogRecentArticles);
}
